import { IAppContextState, INITIAL_STATE, AppDevice, AppMenuWidth, NotificationSetType } from './AppContext.ts';
import { Breakpoint } from '@mui/material';
import { IAuthUserInfo } from '@/types/Auth.ts';
import { IRole } from '@/types/role';
import { IUser } from '@/types/user';
import { IRolePermission } from '@/types/role/permissions.ts';
import { AppUtils } from '@/utils/AppUtils.ts';
import { ProfileTab } from '@/states/profile/index.tsx';
import { INotification } from '@/types/notification';

type AppContextActions =
    | { type: 'setAuthUserInfo'; payload: IAuthUserInfo }
    | { type: 'setAuthUser'; payload: IUser }
    | { type: 'setLoaded'; payload: boolean }
    | { type: 'setMenuOpen'; payload: boolean }
    | { type: 'setBreakPoint'; payload: Breakpoint }
    | { type: 'setDevice'; payload: AppDevice }
    | { type: 'setMenuWidth'; payload: AppMenuWidth }
    | { type: 'setAuthUserRoles'; payload: IRole[] }
    | { type: 'cleanState'; }
    | { type: 'setProfileForm'; payload: ProfileTab }
    | { type: 'setNotifications'; payload: { values: INotification[], type: NotificationSetType } }
    | { type: 'pushNotification'; payload: INotification }
    | { type: 'setWsClient'; payload: WebSocket | null }
    | { type: 'setNewTask'; payload: boolean }
    | { type: 'setActiveTask'; payload: number }

const audio = new Audio('/assets/notification_sound.mp3');
const now = new Date();
audio.volume = 1;

export const AppReducer = (state: IAppContextState, action: AppContextActions): IAppContextState => {
    switch (action.type) {
        case 'setActiveTask':
            return {
                ...state,
                activeTasks: action.payload
            }
        case 'setNewTask':
            return {
                ...state,
                newTask: action.payload
            }
        case 'setAuthUserInfo':
            return {
                ...state,
                auth: action.payload,
            }

        case 'cleanState': {
            return {
                ...INITIAL_STATE
            }
        }

        case 'setLoaded': {
            return {
                ...state,
                loaded: action.payload
            }
        }
        case 'setWsClient': {
            return {
                ...state,
                wsClient: action.payload
            }
        }

        case 'setMenuOpen':
            return {
                ...state,
                menu: { ...state.menu, open: action.payload },
            }

        case 'setMenuWidth':
            return {
                ...state,
                menu: { ...state.menu, width: action.payload },
            }

        case 'setBreakPoint':
            return {
                ...state,
                layout: { ...state.layout, breakpoint: action.payload },
            }

        case 'setDevice':
            return {
                ...state,
                layout: { ...state.layout, device: action.payload },
            }

        case 'setAuthUserRoles': {

            const roles = action.payload;
            const permissions: IRolePermission[] = [];

            roles.forEach((role) => {
                const rolePermissions = Array.from(role?.permissions ?? []);
                for (const permission of rolePermissions) {
                    if (
                        !permissions.some((x) => AppUtils.compareObjects(permission, x))
                    ) {
                        permissions.push(permission);
                    }
                }
            });

            return {
                ...state,
                auth: { ...state.auth, roles, permissions },
            };
        }

        case 'setAuthUser': {
            return { ...state, auth: { ...state.auth, user: action.payload } }
        }

        case 'setNotifications': {
            const { values, type } = action.payload

            switch (type) {
                case 'update': {

                    const arrayCopy = [...state.notifications]
                    for (const item of values) {
                        const index = arrayCopy.findIndex(x => x._id === item._id)
                        if (index + 1) {
                            arrayCopy[index] = item
                        }
                    }

                    return {
                        ...state,
                        notifications: arrayCopy
                    }

                }

                case 'remove': {

                    const arrayCopy = [...state.notifications]
                        .filter((x) => !values.some(i => i._id === x._id))

                    return {
                        ...state,
                        notifications: arrayCopy
                    }
                }
                default: {
                    return {
                        ...state,
                        notifications: values
                    }
                }
            }
        }

        case 'pushNotification': {
            const notification = <INotification>action.payload;
            const exist = state.notifications.some(n => n._id === notification._id);

            if (!localStorage.getItem('nse') && notification.scope === 'notification' && new Date(notification.timestamp) > now && !exist) {
                audio.play();
            }

            return {
                ...state,
                notifications: exist ? state.notifications : state.notifications.concat(notification),
                lastWsNotification: notification
            };
        }

        default:
            return state
    }
}
